/**
 * <accordion-item>
 * ------------------------------------------------------------------
 * A fully accessible, animated accordion Web Component.
 *
 * FEATURES
 * ------------------------------------------------------------------
 * • Click & programmatic control (open / close / toggle)
 * • Group support (only one open per group)
 * • Smooth height animation with ResizeObserver
 * • Responsive-safe (content height recalculates on resize)
 * • ARIA-compliant accessibility
 * • Custom events for open / close / toggle
 *
 * REQUIRED MARKUP
 * ------------------------------------------------------------------
 * <accordion-item data-group="1" data-transition-time="300">
 *   <button
 *     data-accordion-header
 *     aria-controls="panel-1"
 *   >
 *     Accordion Title
 *   </button>
 *
 *   <div id="panel-1">
 *     <div>
 *       Accordion content goes here
 *     </div>
 *   </div>
 * </accordion-item>
 *
 * ATTRIBUTES
 * ------------------------------------------------------------------
 * • open                  → opens accordion
 * • data-group            → groups accordions (exclusive open)
 * • data-transition-time  → animation duration in ms
 *
 * EVENTS
 * ------------------------------------------------------------------
 * • accordion:open
 * • accordion:close
 * • accordion:toggle
 *
 * PUBLIC API
 * ------------------------------------------------------------------
 * • open()
 * • close()
 * • toggle()
 * • isOpen()
 * • opened (getter/setter)
 *
 * USAGE (JS)
 * ------------------------------------------------------------------
 * const acc = document.querySelector('accordion-item');
 * acc.open();
 * acc.close();
 * acc.toggle();
 * acc.isOpen();
 * acc.opened = true;
 *
 * acc.addEventListener('accordion:open', () => console.log('opened'));
 */

class AccordionItem extends HTMLElement {
  static observedAttributes = ["open"];

  #trigger = null;
  #panel = null;
  #groupId = null;
  #transitionTime = null;
  #resizeObserver = null;

  constructor() {
    super();
    this.#init();
  }

  // Reinitialize if elements weren't found during construction
  connectedCallback() {
    if (!this.#trigger || !this.#panel) {
      this.#init();
    }
  }

  // Attribute-based state management
  // Changing the "open" attribute drives all behavior
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "open" && oldValue !== newValue) {
      this.hasAttribute("open") ? this.#handleOpen() : this.#handleClose();
    }
  }

  disconnectedCallback() {
    if (this.#trigger) {
      this.#trigger.removeEventListener("click", this.#handleToggle);
    }
    if (this.#panel) {
      this.#panel.removeEventListener(
        "transitionend",
        this.#handleTransitionEnd,
      );
      this.#resizeObserver?.disconnect();
    }
  }

  #init() {
    // Set trigger for accordion
    this.#trigger = this.querySelector("[data-accordion-header]") || this.firstElementChild;
    if (!this.#trigger) {
      console.warn(`${this.tagName.toLowerCase()}: trigger button not found`);
      return;
    }
    this.#trigger.id ||= this.#generateId('trigger');

    // Set content panel for accordion
    this.#panel = this.#trigger.nextElementSibling;
    if (!this.#panel) {
      console.warn(`${this.tagName.toLowerCase()}: content panel not found"`);
      return;
    }
    this.#panel.id ||= this.#generateId('panel');

    // Set properties from dataset's
    this.#groupId = this.dataset.group;
    this.#transitionTime = this.dataset.transitionTime;

    // Set initial accessibility attributes
    this.#trigger.setAttribute("aria-expanded", this.hasAttribute("open").toString());
    this.#trigger.setAttribute("aria-controls", this.#panel.id);
    this.#panel.setAttribute("role", "region");
    this.#panel.setAttribute("aria-labelledby", this.#trigger.id);
    this.hasAttribute("open")
      ? this.#panel.removeAttribute("hidden")
      : this.#panel.setAttribute("hidden", "");

    // Setup content panel important css
    this.#setupCss();

    // ResizeObserver keeps panel height in sync
    // when content reflows (e.g. responsive text wrapping)
    this.#resizeObserver = new ResizeObserver((entries) => {
      if (!this.hasAttribute("open")) return; // Only update height if accordion is open
      for (const entry of entries) {
        this.#panel.style.height = `${entry.target.scrollHeight}px`;
      }
    });
    if(this.#panel.children.length === 1) {
      this.#resizeObserver.observe(this.#panel.firstElementChild);
    }
    else {
      this.#resizeObserver.observe(this.#panel);
    }

    // Add event listeners
    this.#trigger.addEventListener("click", this.#handleToggle.bind(this));
    this.#panel.addEventListener(
      "transitionend",
      this.#handleTransitionEnd.bind(this),
    );

    // Initialize state
    this.opened = this.hasAttribute("open");
  }

  // Generate unique ID for trigger if not exist
  #generateId(key) {
    return `${this.tagName.toLowerCase()}-${key}-${Math.random().toString(36).slice(2, 9)}`;
  }

  #setupCss() {
    if (this.#panel) {
      this.#panel.style.overflow = "hidden";
      if (this.#transitionTime) {
        this.#panel.style.transition = `height ${this.#transitionTime}ms`;
        this.#panel.style.willChange = "height";
      }
    }
  }

  #handleToggle() {
    const isOpen = this.hasAttribute("open");
    isOpen ? this.removeAttribute("open") : this.setAttribute("open", "");
    this.#dispatch("accordion:toggle", { open: !isOpen });
  }

  #handleOpen() {
    if (this.#groupId) {
      const groupItems = document.querySelectorAll(
        `${this.tagName.toLowerCase()}[data-group="${this.#groupId}"][open]`,
      );
      groupItems?.forEach((item) => {
        if (item !== this) item.removeAttribute("open");
      });
    }
    this.#panel.removeAttribute("hidden");
    this.#trigger.setAttribute("aria-expanded", "true");
    this.#panel.style.height = `${this.#panel.scrollHeight}px`;
    this.#dispatch("accordion:open");
  }

  #handleClose() {
    this.#trigger.setAttribute("aria-expanded", "false");
    this.#panel.style.height = "0px";
    this.#dispatch("accordion:close");
  }

  #handleTransitionEnd() {
    if (!this.hasAttribute("open")) {
      this.#panel.setAttribute("hidden", "");
    }
  }

  // Dispatch custom accordion events
  // These bubble so parent containers can listen
  #dispatch(name, detail = {}) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        composed: true, // allows crossing shadow DOM if used later
        detail: {
          target: this,
          ...detail,
        },
      }),
    );
  }

  /**
   * ------------------------------------------------------------------
   * PUBLIC INSTANCE API
   * ------------------------------------------------------------------
   * These methods and properties are safe for external use.
   */

  open() {
    if (!this.hasAttribute("open")) this.setAttribute("open", "");
  }

  close() {
    if (this.hasAttribute("open")) this.removeAttribute("open");
  }

  toggle() {
    this.#handleToggle();
  }

  isOpen() {
    return this.hasAttribute("open");
  }

  get opened() {
    return this.hasAttribute("open");
  }

  set opened(val) {
    val ? this.#handleOpen() : this.#handleClose();
  }
}

// Register the custom element
if (!customElements.get("accordion-item")) {
  customElements.define("accordion-item", AccordionItem);
}
